import React from 'react';
import { useTranslation } from 'react-i18next';
import { MainMatchesData } from './MainMatchesData';
import {
  FaSquare,
  FaExclamationTriangle,
  FaFlag,
  FaUsers,
  FaHome,
  FaPlane,
  FaHistory,
  FaCalendarAlt,
  FaHandshake,
  FaExchangeAlt,
  FaTimes,
  FaEquals,
  FaChartBar,
  FaThumbsUp,
  FaThumbsDown,
  FaRegLightbulb,
  FaShieldAlt,
  FaBullseye,
  FaFootballBall,
  FaTableTennis,
} from 'react-icons/fa';
import {
  GiSoccerField,
  GiSoccerBall,
  GiWhistle,
  GiCardRandom,
} from 'react-icons/gi';
import { MdCompareArrows, MdOutlineSportsScore } from 'react-icons/md';
import { IoStatsChart } from 'react-icons/io5';
import { MdCategory } from 'react-icons/md';

interface Props {
  selectedItemData: MainMatchesData;
  columnToShow?: 'left' | 'center' | 'right' | 'all';
}

export const GoalPerMinuteAndHead2Head = ({
  selectedItemData,
  columnToShow = 'all',
}: Props) => {
  const { t } = useTranslation();

  if (!selectedItemData) {
    return <div>{t('goalPerMinuteAndHead2Head.noData')}</div>;
  }

  // Determine which columns to show
  const showLeft = columnToShow === 'left' || columnToShow === 'all';
  const showCenter = columnToShow === 'center' || columnToShow === 'all';
  const showRight = columnToShow === 'right' || columnToShow === 'all';

  // Function to get CSS class for goals scored per minute
  const getCellColorClassGoalsScoredPerMinute = (value: number): string => {
    let probabilityClass = '';
    if (value <= 1) {
      probabilityClass = 'dark-red cell-text';
    } else if (value <= 2) {
      probabilityClass = 'light-orange cell-text';
    } else if (value <= 4) {
      probabilityClass = 'light-yellow cell-text';
    } else if (value <= 6) {
      probabilityClass = 'light-green cell-text';
    } else if (value <= 8) {
      probabilityClass = 'medium-green cell-text';
    } else {
      probabilityClass = 'dark-green cell-text';
    }
    return probabilityClass;
  };

  // Function to get CSS class for goals conceded per minute
  const getCellColorClassGoalsConcededPerMinute = (value: number): string => {
    let probabilityClass = '';
    if (value <= 1) {
      probabilityClass = 'medium-green cell-text';
    } else if (value <= 2) {
      probabilityClass = 'light-yellow cell-text';
    } else if (value <= 4) {
      probabilityClass = 'dark-yellow cell-text';
    } else if (value <= 6) {
      probabilityClass = 'light-orange cell-text';
    } else {
      probabilityClass = 'dark-red cell-text';
    }
    return probabilityClass;
  };

  // Function to get CSS class for percentage
  const getCellColorClass = (value: number): string => {
    let probabilityClass = '';
    if (value <= 10) {
      probabilityClass = 'dark-red cell-text';
    } else if (value <= 20) {
      probabilityClass = 'medium-red cell-text';
    } else if (value <= 30) {
      probabilityClass = 'light-red cell-text';
    } else if (value <= 40) {
      probabilityClass = 'dark-orange cell-text';
    } else if (value <= 50) {
      probabilityClass = 'light-orange cell-text';
    } else if (value <= 60) {
      probabilityClass = 'dark-yellow cell-text';
    } else if (value <= 70) {
      probabilityClass = 'light-yellow cell-text';
    } else if (value <= 80) {
      probabilityClass = 'light-green cell-text';
    } else if (value <= 90) {
      probabilityClass = 'medium-green cell-text';
    } else {
      probabilityClass = 'dark-green cell-text';
    }
    return probabilityClass;
  };

  // Function to get CSS class for match result
  const getMatchResultClass = (value: string): string => {
    if (value.includes('(W)'))
      return 'bg-green-600 bg-opacity-50 text-green-300 font-bold';
    if (value.includes('(L)'))
      return 'bg-red-600 bg-opacity-50 text-red-300 font-bold';
    if (value.includes('(N)'))
      return 'bg-yellow-400 bg-opacity-50 text-yellow-300 font-bold';
    return 'text-gray-300';
  };

  // Enhanced header styles
  const getHeaderStyle = (isHomeTeam: boolean) => {
    return isHomeTeam
      ? { background: 'linear-gradient(to right, #065f46, #064e3b)' }
      : { background: 'linear-gradient(to right, #1e40af, #1e3a8a)' };
  };

  // Center header style
  const getCenterHeaderStyle = () => {
    return { background: 'linear-gradient(to right, #1e40af, #312e81)' };
  };

  const formatNotAvailableValue = (value: number | null | undefined) => {
    return value === 0 || value === null || value === undefined ? 'N/A' : value;
  };

  // Component for header with icons
  const HeaderWithIcon = ({
    icon: Icon,
    text,
    style = {},
  }: {
    icon: React.ElementType;
    text: string;
    style?: React.CSSProperties;
  }) => (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        ...style,
      }}
    >
      <Icon style={{ marginRight: '0.5rem' }} /> {text}
    </div>
  );

  // Component for column headers with icons
  const ColumnHeaderWithIcon = ({
    icon: Icon,
    text,
  }: {
    icon: React.ElementType;
    text: string;
  }) => (
    <span
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Icon style={{ marginRight: '0.25rem' }} /> {text}
    </span>
  );

  // Yellow card custom icon with yellow color
  const YellowCardIcon = () => (
    <div
      className="inline-block w-4 h-5 bg-yellow-400 mr-2"
      style={{ borderRadius: '2px' }}
    ></div>
  );

  return (
    <div className="TeamsAverageGoals">
      <div className="last5container">
        {/* LEFT COLUMN - HOME TEAM */}
        {showLeft && (
          <div className="left-table">
            {/* Yellow Cards */}
            <div className="table-container">
              <table className="stats-table">
                <thead>
                  <tr>
                    <th colSpan={3} style={getHeaderStyle(true)}>
                      <div className="flex items-center justify-center">
                        <YellowCardIcon />
                        {t('goalPerMinuteAndHead2Head.yellowCards')}
                      </div>
                    </th>
                  </tr>
                  <tr>
                    <th>
                      <ColumnHeaderWithIcon
                        icon={MdCategory}
                        text={t('goalPerMinuteAndHead2Head.type')}
                      />
                    </th>
                    <th>
                      <ColumnHeaderWithIcon
                        icon={FaChartBar}
                        text={t('goalPerMinuteAndHead2Head.average')}
                      />
                    </th>
                    <th>
                      <ColumnHeaderWithIcon
                        icon={IoStatsChart}
                        text={t('goalPerMinuteAndHead2Head.total')}
                      />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="text-gray-300">
                      <span
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <FaHome style={{ marginRight: '0.25rem' }} />{' '}
                        {t('goalPerMinuteAndHead2Head.homeOnly')}
                      </span>
                    </td>
                    <td className="text-yellow-300 font-bold">
                      {formatNotAvailableValue(
                        selectedItemData.HomeYellowCards.AverageAtHome,
                      )}
                    </td>
                    <td>-</td>
                  </tr>
                  <tr>
                    <td className="text-gray-300">
                      <span
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <IoStatsChart style={{ marginRight: '0.25rem' }} />{' '}
                        {t('goalPerMinuteAndHead2Head.allMatches')}
                      </span>
                    </td>
                    <td className="text-yellow-300 font-bold">
                      {formatNotAvailableValue(
                        selectedItemData.HomeYellowCards.AverageTotal,
                      )}
                    </td>
                    <td className="text-yellow-300 font-bold">
                      {formatNotAvailableValue(
                        selectedItemData.HomeYellowCards.Total,
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Fouls */}
            <div className="table-container">
              <table className="stats-table">
                <thead>
                  <tr>
                    <th colSpan={3} style={getHeaderStyle(true)}>
                      <HeaderWithIcon
                        icon={GiWhistle}
                        text={t('goalPerMinuteAndHead2Head.foulsPerMatch')}
                      />
                    </th>
                  </tr>
                  <tr>
                    <th>
                      <ColumnHeaderWithIcon
                        icon={MdCategory}
                        text={t('goalPerMinuteAndHead2Head.type')}
                      />
                    </th>
                    <th>
                      <ColumnHeaderWithIcon
                        icon={FaExclamationTriangle}
                        text={t('goalPerMinuteAndHead2Head.committed')}
                      />
                    </th>
                    <th>
                      <ColumnHeaderWithIcon
                        icon={FaThumbsUp}
                        text={t('goalPerMinuteAndHead2Head.suffered')}
                      />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="text-gray-300">
                      <span
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <FaHome style={{ marginRight: '0.25rem' }} />{' '}
                        {t('goalPerMinuteAndHead2Head.homeOnly')}
                      </span>
                    </td>
                    <td className="text-red-300 font-bold">
                      {formatNotAvailableValue(
                        selectedItemData.HomeFouls.AverageAtHomeOrAway,
                      )}
                    </td>
                    <td className="text-teal-300 font-bold">
                      {formatNotAvailableValue(
                        selectedItemData.HomeFouls.AverageVsAtHomeOrAway,
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className="text-gray-300">
                      <span
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <IoStatsChart style={{ marginRight: '0.25rem' }} />{' '}
                        {t('goalPerMinuteAndHead2Head.allMatches')}
                      </span>
                    </td>
                    <td className="text-red-300 font-bold">
                      {formatNotAvailableValue(
                        selectedItemData.HomeFouls.AverageTotal,
                      )}
                    </td>
                    <td className="text-teal-300 font-bold">
                      {formatNotAvailableValue(
                        selectedItemData.HomeFouls.AverageTotalVs,
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Corners */}
            <div className="table-container">
              <table className="stats-table">
                <thead>
                  <tr>
                    <th colSpan={3} style={getHeaderStyle(true)}>
                      <HeaderWithIcon
                        icon={FaFlag}
                        text={t('goalPerMinuteAndHead2Head.cornersPerMatch')}
                      />
                    </th>
                  </tr>
                  <tr>
                    <th>
                      <ColumnHeaderWithIcon
                        icon={MdCategory}
                        text={t('goalPerMinuteAndHead2Head.type')}
                      />
                    </th>
                    <th>
                      <ColumnHeaderWithIcon
                        icon={FaThumbsUp}
                        text={t('goalPerMinuteAndHead2Head.for')}
                      />
                    </th>
                    <th>
                      <ColumnHeaderWithIcon
                        icon={FaThumbsDown}
                        text={t('goalPerMinuteAndHead2Head.against')}
                      />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="text-gray-300">
                      <span
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <FaHome style={{ marginRight: '0.25rem' }} />{' '}
                        {t('goalPerMinuteAndHead2Head.homeOnly')}
                      </span>
                    </td>
                    <td className="text-teal-300 font-bold">
                      {formatNotAvailableValue(
                        selectedItemData.HomeCorners.AverageAtHomeOrAway,
                      )}
                    </td>
                    <td className="text-red-300 font-bold">
                      {formatNotAvailableValue(
                        selectedItemData.HomeCorners.AverageVsAtHomeOrAway,
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className="text-gray-300">
                      <span
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <IoStatsChart style={{ marginRight: '0.25rem' }} />{' '}
                        {t('goalPerMinuteAndHead2Head.allMatches')}
                      </span>
                    </td>
                    <td className="text-teal-300 font-bold">
                      {formatNotAvailableValue(
                        selectedItemData.HomeCorners.AverageTotal,
                      )}
                    </td>
                    <td className="text-red-300 font-bold">
                      {formatNotAvailableValue(
                        selectedItemData.HomeCorners.AverageTotalVs,
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* CENTER COLUMN - HEAD TO HEAD */}
        {showCenter && (
          <div className="center-table">
            <div className="table-container">
              <table className="h2h-table">
                <thead>
                  <tr>
                    <th
                      colSpan={5}
                      className="bg-gradient-to-r from-teal-800 to-indigo-900"
                    >
                      <HeaderWithIcon
                        icon={MdCompareArrows}
                        text={t('goalPerMinuteAndHead2Head.headToHead')}
                      />
                    </th>
                  </tr>
                  <tr>
                    <th
                      colSpan={2}
                      style={{ backgroundColor: 'rgba(45, 212, 191, 0.1)' }}
                    >
                      {t('goalPerMinuteAndHead2Head.homeTeam')}
                    </th>
                    <th style={{ backgroundColor: 'rgba(0, 0, 0, 0.3)' }}>
                      <HeaderWithIcon
                        icon={FaUsers}
                        text={t('goalPerMinuteAndHead2Head.opponent')}
                      />
                    </th>
                    <th
                      colSpan={2}
                      style={{ backgroundColor: 'rgba(79, 70, 229, 0.1)' }}
                    >
                      {t('goalPerMinuteAndHead2Head.awayTeam')}
                    </th>
                  </tr>
                  <tr>
                    <th>
                      <ColumnHeaderWithIcon icon={FaHome} text="" />
                    </th>
                    <th>
                      <ColumnHeaderWithIcon icon={FaPlane} text="" />
                    </th>
                    <th></th>
                    <th>
                      <ColumnHeaderWithIcon icon={FaHome} text="" />
                    </th>
                    <th>
                      <ColumnHeaderWithIcon icon={FaPlane} text="" />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {selectedItemData.StandingOpponents.map((opponent, index) => (
                    <tr key={index} className="hover:bg-slate-700">
                      <td
                        className={getMatchResultClass(
                          opponent.HomeTeamHomeResult,
                        )}
                      >
                        {opponent.HomeTeamHomeResult.replace(/\(.*?\)/g, '')}
                      </td>
                      <td
                        className={getMatchResultClass(
                          opponent.HomeTeamAwayResult,
                        )}
                      >
                        {opponent.HomeTeamAwayResult.replace(/\(.*?\)/g, '')}
                      </td>
                      <td
                        className="opponent-name text-gray-300 text-sm"
                        title={opponent.Opponent}
                      >
                        {opponent.Opponent.length > 25
                          ? opponent.Opponent.substring(0, 22) + '...'
                          : opponent.Opponent}
                      </td>

                      <td
                        className={getMatchResultClass(
                          opponent.AwayTeamHomeResult,
                        )}
                      >
                        {opponent.AwayTeamHomeResult.replace(/\(.*?\)/g, '')}
                      </td>
                      <td
                        className={getMatchResultClass(
                          opponent.AwayTeamAwayResult,
                        )}
                      >
                        {opponent.AwayTeamAwayResult.replace(/\(.*?\)/g, '')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* RIGHT COLUMN - AWAY TEAM */}
        {showRight && (
          <div className="right-table">
            {/* Yellow Cards */}
            <div className="table-container">
              <table className="stats-table">
                <thead>
                  <tr>
                    <th colSpan={3} style={getHeaderStyle(false)}>
                      <div className="flex items-center justify-center">
                        <YellowCardIcon />
                        {t('goalPerMinuteAndHead2Head.yellowCards')}
                      </div>
                    </th>
                  </tr>
                  <tr>
                    <th>
                      <ColumnHeaderWithIcon
                        icon={MdCategory}
                        text={t('goalPerMinuteAndHead2Head.type')}
                      />
                    </th>
                    <th>
                      <ColumnHeaderWithIcon
                        icon={FaChartBar}
                        text={t('goalPerMinuteAndHead2Head.average')}
                      />
                    </th>
                    <th>
                      <ColumnHeaderWithIcon
                        icon={IoStatsChart}
                        text={t('goalPerMinuteAndHead2Head.total')}
                      />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="text-gray-300">
                      <span
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <FaPlane style={{ marginRight: '0.25rem' }} />{' '}
                        {t('goalPerMinuteAndHead2Head.awayOnly')}
                      </span>
                    </td>
                    <td className="text-yellow-300 font-bold">
                      {formatNotAvailableValue(
                        selectedItemData.AwayYellowCards.AverageAtHome,
                      )}
                    </td>
                    <td>-</td>
                  </tr>
                  <tr>
                    <td className="text-gray-300">
                      <span
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <IoStatsChart style={{ marginRight: '0.25rem' }} />{' '}
                        {t('goalPerMinuteAndHead2Head.allMatches')}
                      </span>
                    </td>
                    <td className="text-yellow-300 font-bold">
                      {formatNotAvailableValue(
                        selectedItemData.AwayYellowCards.AverageTotal,
                      )}
                    </td>
                    <td className="text-yellow-300 font-bold">
                      {formatNotAvailableValue(
                        selectedItemData.AwayYellowCards.Total,
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Fouls */}
            <div className="table-container">
              <table className="stats-table">
                <thead>
                  <tr>
                    <th colSpan={3} style={getHeaderStyle(false)}>
                      <HeaderWithIcon
                        icon={GiWhistle}
                        text={t('goalPerMinuteAndHead2Head.foulsPerMatch')}
                      />
                    </th>
                  </tr>
                  <tr>
                    <th>
                      <ColumnHeaderWithIcon
                        icon={MdCategory}
                        text={t('goalPerMinuteAndHead2Head.type')}
                      />
                    </th>
                    <th>
                      <ColumnHeaderWithIcon
                        icon={FaExclamationTriangle}
                        text={t('goalPerMinuteAndHead2Head.committed')}
                      />
                    </th>
                    <th>
                      <ColumnHeaderWithIcon
                        icon={FaThumbsUp}
                        text={t('goalPerMinuteAndHead2Head.suffered')}
                      />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="text-gray-300">
                      <span
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <FaPlane style={{ marginRight: '0.25rem' }} />{' '}
                        {t('goalPerMinuteAndHead2Head.awayOnly')}
                      </span>
                    </td>
                    <td className="text-red-300 font-bold">
                      {formatNotAvailableValue(
                        selectedItemData.AwayFouls.AverageAtHomeOrAway,
                      )}
                    </td>
                    <td className="text-teal-300 font-bold">
                      {formatNotAvailableValue(
                        selectedItemData.AwayFouls.AverageVsAtHomeOrAway,
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className="text-gray-300">
                      <span
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <IoStatsChart style={{ marginRight: '0.25rem' }} />{' '}
                        {t('goalPerMinuteAndHead2Head.allMatches')}
                      </span>
                    </td>
                    <td className="text-red-300 font-bold">
                      {formatNotAvailableValue(
                        selectedItemData.AwayFouls.AverageTotal,
                      )}
                    </td>
                    <td className="text-teal-300 font-bold">
                      {formatNotAvailableValue(
                        selectedItemData.AwayFouls.AverageTotalVs,
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Corners */}
            <div className="table-container">
              <table className="stats-table">
                <thead>
                  <tr>
                    <th colSpan={3} style={getHeaderStyle(false)}>
                      <HeaderWithIcon
                        icon={FaFlag}
                        text={t('goalPerMinuteAndHead2Head.cornersPerMatch')}
                      />
                    </th>
                  </tr>
                  <tr>
                    <th>
                      <ColumnHeaderWithIcon
                        icon={MdCategory}
                        text={t('goalPerMinuteAndHead2Head.type')}
                      />
                    </th>
                    <th>
                      <ColumnHeaderWithIcon
                        icon={FaThumbsUp}
                        text={t('goalPerMinuteAndHead2Head.for')}
                      />
                    </th>
                    <th>
                      <ColumnHeaderWithIcon
                        icon={FaThumbsDown}
                        text={t('goalPerMinuteAndHead2Head.against')}
                      />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="text-gray-300">
                      <span
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <FaPlane style={{ marginRight: '0.25rem' }} />{' '}
                        {t('goalPerMinuteAndHead2Head.awayOnly')}
                      </span>
                    </td>
                    <td className="text-teal-300 font-bold">
                      {formatNotAvailableValue(
                        selectedItemData.AwayCorners.AverageAtHomeOrAway,
                      )}
                    </td>
                    <td className="text-red-300 font-bold">
                      {formatNotAvailableValue(
                        selectedItemData.AwayCorners.AverageVsAtHomeOrAway,
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td className="text-gray-300">
                      <span
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <IoStatsChart style={{ marginRight: '0.25rem' }} />{' '}
                        {t('goalPerMinuteAndHead2Head.allMatches')}
                      </span>
                    </td>
                    <td className="text-teal-300 font-bold">
                      {formatNotAvailableValue(
                        selectedItemData.AwayCorners.AverageTotal,
                      )}
                    </td>
                    <td className="text-red-300 font-bold">
                      {formatNotAvailableValue(
                        selectedItemData.AwayCorners.AverageTotalVs,
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
